/**
 * localStorage persistence for user-resized column widths, keyed by table
 * type. Mirrors the silent try/catch helper pattern of
 * `getStoredDateRange`/`setStoredDateRange` in `ui/date-range-filter.tsx`.
 *
 * Key scheme: `editable-table:${storageKey}:column-widths` →
 * `Record<col.data, widthPx>`. Stale entries for columns that no longer
 * exist are harmless — readers simply ignore unknown keys.
 */

const keyFor = (storageKey: string) => `editable-table:${storageKey}:column-widths`;

/** Smallest width a column can take — drag clamp floor and storage clamp floor. */
export const MIN_COLUMN_WIDTH = 50;
/**
 * Storage/drag clamp ceiling. Wider than any real column; exists so a
 * corrupt or hand-edited localStorage entry can't render a kilometer-wide
 * column (the colgroup applies stored values literally).
 */
export const MAX_COLUMN_WIDTH = 4000;

const clampWidth = (value: number) =>
  Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, value));

/**
 * Read persisted column widths. Returns `{}` when missing or corrupt.
 * Individual non-finite / non-positive values are dropped so one bad
 * entry doesn't discard the rest; finite values are clamped into
 * [MIN_COLUMN_WIDTH, MAX_COLUMN_WIDTH].
 */
export function getStoredColumnWidths(storageKey: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(keyFor(storageKey));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const widths: Record<string, number> = {};
    for (const [col, value] of Object.entries(parsed)) {
      if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        widths[col] = clampWidth(value);
      }
    }
    return widths;
  } catch {
    // corrupt data – ignore
    return {};
  }
}

/** Write column widths. An empty record removes the key entirely. */
export function setStoredColumnWidths(storageKey: string, widths: Record<string, number>): void {
  try {
    if (Object.keys(widths).length === 0) {
      localStorage.removeItem(keyFor(storageKey));
    } else {
      localStorage.setItem(keyFor(storageKey), JSON.stringify(widths));
    }
  } catch {
    // storage full or unavailable – ignore
  }
}
